<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: User Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.userName')"
                v-model.trim="filterOptions.userName"
                :maxlength="100"
                @input="limitInputLength('userName', $event, 100)"
              />
              <!-- End:: User Name Input -->

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.startDate')"
                v-model.trim="filterOptions.from_date"
              />
              <!-- End:: Start Date Input -->

              <!-- Start:: End Date Input -->
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.endDate')"
                v-model.trim="filterOptions.to_date"
              />
              <!-- End:: End Date Input -->

              <!-- Start:: Total Order Value From Input -->
              <base-input
                col="6"
                type="number"
                :placeholder="$t('PLACEHOLDERS.totalOrderValueFrom')"
                v-model.trim="filterOptions.totalFrom"
              />
              <!-- End:: Total Order Value From Input -->

              <!-- Start:: Total Order Value To Input -->
              <base-input
                col="6"
                type="number"
                :placeholder="$t('PLACEHOLDERS.totalOrderValueTo')"
                v-model.trim="filterOptions.totalTo"
              />
              <!-- End:: Total Order Value To Input -->
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="isWaitingRequest"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.financialReports") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <div class="export_dropdown">
            <a-dropdown>
              <a-menu slot="overlay" @click="handleExportClick">
                <a-menu-item key="pdf">
                  <i class="fal fa-file-pdf"></i>
                  {{ $t("BUTTONS.downloadPdf") }}
                </a-menu-item>
                <a-menu-item key="excel">
                  <i class="fal fa-file-excel"></i>
                  {{ $t("BUTTONS.downloadExcel") }}
                </a-menu-item>
              </a-menu>
              <a-button style="margin-left: 8px">
                <i class="fal fa-download mx-2"></i>
                {{ $t("BUTTONS.exportResults") }}
              </a-button>
            </a-dropdown>
          </div>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <template v-slot:[`item.id`]="{ item, index }">
          <p class="blue-grey--text text--darken-1 fs-3" v-if="!item.id">-</p>
          <p v-else>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>

        <!-- Start:: Order Number -->
        <template v-slot:[`item.order_number`]="{ item }">
          <p v-if="item.order_number">{{ item.order_number }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: Order Number -->

        <!-- Start:: User Name -->
        <template v-slot:[`item.user_name`]="{ item }">
          <p v-if="item.user?.name">{{ item.user.name }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: User Name -->

        <!-- Start:: Subtotal -->
        <template v-slot:[`item.subtotal`]="{ item }">
          <p v-if="item.subtotal">{{ formatNumber(item.subtotal) }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: Subtotal -->

        <!-- Start:: Tax Amount -->
        <template v-slot:[`item.tax_amount`]="{ item }">
          <p v-if="item.tax_amount">{{ formatNumber(item.tax_amount) }}</p>
          <p class="blue-grey--text text--darken-1 fs-3" v-else>-</p>
        </template>
        <!-- End:: Tax Amount -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!-- ==== Start:: Summary Totals ==== -->
      <div class="table_content mt-5">
        <v-simple-table class="stat-table">
          <template v-slot:default>
            <thead class="all-stat">
              <tr>
                <th class="text-center" style="color: white !important">
                  {{ $t("PLACEHOLDERS.totalSubtotal") }}
                </th>
                <th class="text-center" style="color: white !important">
                  {{ $t("PLACEHOLDERS.totalTax") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="text-center">
                <td>{{ formatNumber(summary.total_subtotal) }}</td>
                <td>{{ formatNumber(summary.total_tax) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
      <!-- ==== End:: Summary Totals ==== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllOrdersFinancialReports",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        userName: null,
        from_date: null,
        to_date: null,
        totalFrom: null,
        totalTo: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Clients.serialNumber"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderNum"),
          value: "order_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.userName"),
          value: "user_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.totalProducts"),
          value: "subtotal",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.totalTax"),
          value: "tax_amount",
          align: "center",
          sortable: false,
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Summary Data
      summary: {
        total_subtotal: 0,
        total_tax: 0,
      },
      // End:: Summary Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page || 1;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    limitInputLength(field, event, maxLength) {
      const value = event.target ? event.target.value : event;
      if (value && value.length > maxLength) {
        const limitedValue = value.slice(0, maxLength);
        this.filterOptions[field] = limitedValue;
        if (event.target) {
          event.target.value = limitedValue;
        }
      }
    },

    formatNumber(value) {
      if (!value) return "0.00";
      return parseFloat(value).toFixed(2);
    },

    // Start:: Handle Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/orders-financial-reports/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.userName = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;
      this.filterOptions.totalFrom = null;
      this.filterOptions.totalTo = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/orders-financial-reports/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handle Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },

    async setTableRows() {
      this.loading = true;
      try {
        // Build params for orders endpoint
        const params = {
          page: this.paginations.current_page,
          per_page: this.paginations.items_per_page,
        };

        // Add filters if provided
        if (this.filterOptions.userName) {
          params["filter[user_name]"] = this.filterOptions.userName;
        }
        if (this.filterOptions.from_date) {
          params["filter[date_from]"] = this.filterOptions.from_date;
        }
        if (this.filterOptions.to_date) {
          params["filter[date_to]"] = this.filterOptions.to_date;
        }
        if (this.filterOptions.totalFrom) {
          params["filter[total_min]"] = this.filterOptions.totalFrom;
        }
        if (this.filterOptions.totalTo) {
          params["filter[total_max]"] = this.filterOptions.totalTo;
        }

        // Use orders endpoint for all queries (supports filtering and pagination)
        let res = await this.$axios({
          method: "GET",
          url: "orders",
          params: params,
        });

        this.loading = false;

        // Map API response to table format
        this.tableRows = res.data.data?.map((item) => ({
          ...item,
          user_name: item.user?.name || null,
        }));

        // Calculate summary from table rows
        this.summary = {
          total_subtotal: this.tableRows.reduce(
            (sum, item) => sum + parseFloat(item.subtotal || 0),
            0
          ),
          total_tax: this.tableRows.reduce(
            (sum, item) => sum + parseFloat(item.tax_amount || 0),
            0
          ),
        };

        // Update pagination
        this.paginations.last_page = res.data.pagination?.last_page || 1;
        this.paginations.items_per_page = res.data.pagination?.per_page || 10;
        this.paginations.current_page = res.data.pagination?.current_page || 1;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.error")
        );
      }
    },
    // End:: Set Table Rows

    // Start:: Export Functions
    handleExportClick({ key }) {
      if (key === "pdf") {
        this.downloadPdf();
      } else if (key === "excel") {
        this.downloadExcel();
      }
    },

    async downloadPdf() {
      try {
        const params = {};
        if (this.filterOptions.from_date) {
          params.start_date = this.filterOptions.from_date;
        }
        if (this.filterOptions.to_date) {
          params.end_date = this.filterOptions.to_date;
        }
        if (this.filterOptions.userName) {
          params.user_name = this.filterOptions.userName;
        }

        const response = await this.$axios({
          method: "GET",
          url: "financial-reports/export/pdf",
          params: params,
          responseType: "blob",
        });

        // Create a blob from the response
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        let dateRange =
          params.start_date && params.end_date
            ? `${params.start_date}_${params.end_date}`
            : params.start_date
            ? `${params.start_date}`
            : params.end_date
            ? `_${params.end_date}`
            : "all";
        if (params.user_name) {
          dateRange += `_user_${params.user_name}`;
        }
        link.setAttribute("download", `financial-reports_${dateRange}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$message.success(this.$t("MESSAGES.downloadSuccess"));
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.downloadError")
        );
      }
    },

    async downloadExcel() {
      try {
        const params = {};
        if (this.filterOptions.from_date) {
          params.start_date = this.filterOptions.from_date;
        }
        if (this.filterOptions.to_date) {
          params.end_date = this.filterOptions.to_date;
        }
        if (this.filterOptions.userName) {
          params.user_name = this.filterOptions.userName;
        }

        const response = await this.$axios({
          method: "GET",
          url: "financial-reports/export/excel",
          params: params,
          responseType: "blob",
        });

        // Create a blob from the response
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        let dateRange =
          params.start_date && params.end_date
            ? `${params.start_date}_${params.end_date}`
            : params.start_date
            ? `${params.start_date}`
            : params.end_date
            ? `_${params.end_date}`
            : "all";
        if (params.user_name) {
          dateRange += `_user_${params.user_name}`;
        }
        link.setAttribute("download", `financial-reports_${dateRange}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$message.success(this.$t("MESSAGES.downloadSuccess"));
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.downloadError")
        );
      }
    },
    // End:: Export Functions
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.all-stat {
  background: var(--main_theme_clr);
}

.all-stat th {
  color: white !important;
  font-size: 16px !important;
  font-weight: 800;
}

.all-stat td {
  color: white;
  font-size: 18px !important;
  font-weight: 600;
}

.export_dropdown {
  display: inline-block;
}
</style>
